import { Menu, Dropdown, Button, Modal, Input, Form } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { v4 } from "uuid";
import { Pages } from "../../../AppRouter";
import ApiService from "../../../core/service/api-service";
import { useTranslation } from "react-i18next";
import { DataGridProvider, useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import RowMenu from "../../../core/components/data-grid/RowMenu";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import ReportPreview from "../ReportPreview";
import ReportVersionsGrid from "../ReportVersionsGrid";
import { useModal } from "../../../core/context/ModalContext";
import ReportModal from "../../../pages/report/ReportModal";
import { ReportTemplateLanguages } from '../../hospital/hospital-data-grid/HospitalRowMenu';
import { useState } from "react";
import { studyTaskTypeKey } from "../../../enums/StudyTaskTypeEnum";

const ButtonGroup = Button.Group;  // v5: antd/lib/button/button-group derin yolu yerine resmî API

const { TextArea } = Input;

export default function ReportRowMenu({ row }) {
    const { t } = useTranslation();
    const { refreshDataGrid } = useDataGrid();
    const { hasPermission } = useUser();
    const { openModal, closeModal } = useModal()
    const [reAssessmentModalVisible, setReAssessmentModalVisible] = useState(false);
    const [isRemoveConfirmation, setIsRemoveConfirmation] = useState(false);
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [confirmForm] = Form.useForm();

    const openReportPreviewModal = (studyId, requestId, reportId) => {
        openModal(t('Report Preview'), (
            <ReportPreview
                studyId={studyId}
                requestId={requestId}
                reportId={reportId}
            />
        ))
    }

    const handleReAssessmentRequest = async () => {
        try {
            const values = await form.validateFields();
            const response = await ApiService.StudyTask.Create({
                studyId: row.studyId,
                requestId: row.requestId,
                taskType: isRemoveConfirmation ? 3 : 2, // RE_ASSESSMENT
                description: values.description
            });
            if (response.success) {
                setReAssessmentModalVisible(false);
                form.resetFields();
                refreshDataGrid();
            }
        } catch (error) {
            console.error('Validation failed:', error);
        }
    }

    const handleConfirmClick = async () => {
        if (row.studyTasks && row.studyTasks.length > 0) {
            setConfirmModalVisible(true);
        } else {
            await ApiService.Report.ConfirmReport(row.studyId, row.requestId, row.id);
            refreshDataGrid();
        }
    }

    const handleConfirmReport = async () => {
        try {
            const values = await confirmForm.validateFields();
            await ApiService.Report.ConfirmReport(row.studyId, row.requestId, row.id, { description: values.description });
            setConfirmModalVisible(false);
            confirmForm.resetFields();
            refreshDataGrid();
        } catch (error) {
            console.error('Validation failed:', error);
        }
    }

    return (
        <>
            <RowMenu text={row.id}>
                <Menu>
                    {
                        hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT) && (
                            <Menu.Item
                                key={'add-request-report'}
                                onClick={() => {
                                    openModal(t('Report'), (
                                        <ReportModal
                                            studyId={row.studyId}
                                            requestId={row.requestId}
                                        />
                                    ), null, window.innerWidth * 0.98, 20)
                                }}>
                                <div>
                                    {t('Request & Reports') + ' (R)'}
                                </div>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.REPORT_LIST) && (
                            <Menu.Item
                                key={v4()}
                                onClick={() => { openReportPreviewModal(row.studyId, row.requestId, row.id) }}
                            >
                                {t('Report Preview')}
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.REPORT_CONFIRM) && !row.isConfirm && (
                            <Menu.Item
                                key={v4()}>
                                <div onClick={handleConfirmClick}>
                                    {t('Confirm Report')}
                                </div>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.REPORT_REMOVE_CONFIRM) && row.isConfirm && (
                            <Menu.Item
                                key={v4()}>
                                <div
                                    onClick={async () => {
                                        setIsRemoveConfirmation(true);
                                        setReAssessmentModalVisible(true);
                                    }}
                                >
                                    {t('Remove Confirmation')}
                                </div>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.REPORT_DOWNLOAD_PDF) && row.isConfirm && (
                            <Menu.Item
                                key={v4()}
                            >
                                <ButtonGroup>
                                    <Button
                                        size="small"
                                        style={{ padding: '0 8px', width: '180px' }}
                                        onClick={async (e) => {
                                            const response = await ApiService.ReportGenerator.GetReportPdf(row.studyId, row.requestId, row.id);
                                            if (response.success) {
                                                ApiService.downloadFile(response.data, response.fileName)
                                            }
                                        }}
                                    >
                                        {t('Download PDF')}
                                    </Button>
                                    {
                                        row?.reportOutputTemplates?.length > 1 && (
                                            <Dropdown
                                                overlay={
                                                    <Menu
                                                        onClick={async ({ key }) => {
                                                            const response = await ApiService.ReportGenerator.GetReportPdfWithLanguage(row.studyId, row.requestId, row.id, key);
                                                            if (response.success) {
                                                                ApiService.downloadFile(response.data, response.fileName)
                                                            }
                                                        }}
                                                    >
                                                        {
                                                            row?.reportOutputTemplates?.map(temp => (
                                                                <Menu.Item key={temp.language}>
                                                                    {t(`lang_${temp.language}`)}
                                                                </Menu.Item>
                                                            ))
                                                        }
                                                    </Menu>
                                                }
                                                trigger={['click']}
                                            >
                                                <Button
                                                    size="small"
                                                    style={{ padding: '0 8px' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <DownOutlined />
                                                </Button>
                                            </Dropdown>
                                        )
                                    }
                                </ButtonGroup>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.REPORT_DOWNLOAD_PDF) && row.isConfirm && (
                            <Menu.Item
                                key={v4()}
                            >
                                <ButtonGroup>
                                    <Button
                                        size="small"
                                        style={{ padding: '0 8px', width: '180px' }}
                                        onClick={async (e) => {
                                            const response = await ApiService.ReportGenerator.GetReportWord(row.studyId, row.requestId, row.id)
                                            if (response.success) {
                                                ApiService.downloadFile(response.data, response.fileName)
                                            }
                                        }}
                                    >
                                        {t('Download Word')}
                                    </Button>
                                    {
                                        row?.reportOutputTemplates?.length > 1 && (
                                            <Dropdown
                                                overlay={
                                                    <Menu
                                                        onClick={async ({ key }) => {
                                                            const response = await ApiService.ReportGenerator.GetReportWordWithLanguage(row.studyId, row.requestId, row.id, key);
                                                            if (response.success) {
                                                                ApiService.downloadFile(response.data, response.fileName)
                                                            }
                                                        }}
                                                    >
                                                        {
                                                            row?.reportOutputTemplates?.map(temp => (
                                                                <Menu.Item key={temp.language}>
                                                                    {t(`lang_${temp.language}`)}
                                                                </Menu.Item>
                                                            ))
                                                        }
                                                    </Menu>
                                                }
                                                trigger={['click']}
                                            >
                                                <Button
                                                    size="small"
                                                    style={{ padding: '0 8px' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <DownOutlined />
                                                </Button>
                                            </Dropdown>
                                        )
                                    }
                                </ButtonGroup>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.STUDY_REQUEST_DELETE) && !row.isConfirm && !row.isRequestDeleted && (
                            <Menu.Item
                                key={v4()}
                            >
                                <div
                                    onClick={async () => {
                                        const response = await ApiService.Request.DeleteRequest(row.studyId, row.requestId)
                                        if (response.success) {
                                            refreshDataGrid()
                                        }
                                    }}
                                >
                                    {t('Delete')}
                                </div>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.STUDY_REQUEST_RESTORE_DELETED) && row.isRequestDeleted && (
                            <Menu.Item
                                key={v4()}
                            >
                                <div
                                    onClick={async () => {
                                        const response = await ApiService.Request.RestoreRequest(row.studyId, row.requestId)
                                        if (response.success) {
                                            refreshDataGrid()
                                        }
                                    }}
                                >
                                    {t('Restore')}
                                </div>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.REPORT_VERSIONS) && (
                            <Menu.Item
                                key={'report-versions'}
                                // Versiyon yalnızca var olan rapor düzenlenince oluşur; hiç düzenlenmemiş
                                // raporda açılacak bir şey yok → gizlemek yerine pasif göster.
                                disabled={!row.reportVersionCount}
                                onClick={() => {
                                    openModal(t('Report Versions'), (
                                        <DataGridProvider>
                                            <ReportVersionsGrid
                                                studyId={row.studyId}
                                                reportId={row.id}
                                                requestId={row.requestId}
                                            />
                                        </DataGridProvider>
                                    ), null, window.innerWidth * 0.80, 20)
                                }}
                            >
                                {t('Report Versions')}
                            </Menu.Item>
                        )
                    }
                    {
                        (hasPermission(PERMISSIONS.STUDY_TASK_MANAGER) || hasPermission(PERMISSIONS.STUDY_TASK_CREATE_RE_ASSESSMENT)) && row.isConfirm && (
                            <Menu.Item
                                key={v4()}
                            >
                                <div onClick={() => {
                                    setIsRemoveConfirmation(false);
                                    setReAssessmentModalVisible(true);
                                }}>
                                    {t('Re-Assessment Request')}
                                </div>
                            </Menu.Item>
                        )
                    }
                </Menu>
            </RowMenu>
            <Modal
                title={t(isRemoveConfirmation ? 'Remove Confirmation' : 'Re-Assessment Request')}
                open={reAssessmentModalVisible}
                onOk={handleReAssessmentRequest}
                onCancel={() => {
                    setReAssessmentModalVisible(false);
                    setIsRemoveConfirmation(false);
                    form.resetFields();
                }}
                okText={t('Submit')}
                cancelText={t('Cancel')}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="description"
                        label={t('Description')}
                        rules={[{ required: true, message: t('Please enter a description') }]}
                    >
                        <TextArea rows={4} placeholder={t(isRemoveConfirmation ? 'Enter reason for removing confirmation' : 'Enter description for re-assessment request')} />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title={t('Confirm Report - Open Task Exists')}
                open={confirmModalVisible}
                onCancel={() => {
                    setConfirmModalVisible(false);
                    confirmForm.resetFields();
                }}
                onOk={handleConfirmReport}
                okText={t('Confirm')}
                cancelText={t('Cancel')}
            >
                <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff7e6', borderRadius: 4, border: '1px solid #ffd591' }}>
                    <strong>{t('Open Tasks')}:</strong>
                    {row.studyTasks?.map((task, idx) => (
                        <div key={idx} style={{ marginTop: 8 }}>
                            <div style={{ fontWeight: 500 }}>{t(studyTaskTypeKey(task.taskType))}</div>
                            {task.description && <div style={{ fontSize: 12, color: '#666' }}>{task.description}</div>}
                        </div>
                    ))}
                </div>
                <Form form={confirmForm} layout="vertical">
                    <Form.Item
                        name="description"
                        label={t('Please explain how the open task(s) will be resolved')}
                        rules={[{ required: true, message: t('Please enter description') }]}
                    >
                        <TextArea rows={4} placeholder={t('Explain how you resolved or will handle the open task(s)')} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )
}
